import React, { useState, useEffect } from "react";
import Dialog from "../../dialog/Dialog";
import Toster from "../../common/Toster";
import Pagination from "../../common/Pagination";
import {
  AdminPageLayout,
  AdminPageHeader,
  AdminPageCard,
  AdminPageCardToolbar,
} from "../../common/AdminPageLayout";
import { ADMIN_PAGE_META } from "../../common/adminPageMeta";
import {
  AdminTable,
  AdminTableHead,
  AdminTableTh,
  AdminTableBody,
  AdminTableRow,
  AdminTableCell,
  AdminTableFooter,
  AdminTableToolbar,
  TableSearchInput,
  TableEmptyRow,
  EditActionButton,
  DeleteActionButton,
  TableActionsCell,
  StatusBadge,
  getStatusVariant,
} from "../../common/adminTable";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import * as destinationService from "./service";
import useTableSearch from "../../../hooks/useTableSearch";
import FilePreview from "../../FilePreview/FilePreview";
import PlaceSearchInput from "../../common/PlaceSearchInput";
import ToggleButton from "../../common/ToggleButton";
import ImageInput from "../../common/ImageInput";

const DestinationThumbPreview = ({ destination }) => {
  const [hasError, setHasError] = React.useState(false);
  const hasImage = destination?.thumb_image_url && !hasError;

  if (!hasImage) {
    return (
      <div
        className="flex h-7 w-14 items-center justify-center rounded-md border border-dashed border-gray-200 bg-gray-50 text-[10px] font-medium text-gray-400"
        title="No images"
      >
        No images
      </div>
    );
  }

  return (
    <img
      src={destination.thumb_image_url}
      alt="Destination preview"
      className="h-7 w-7 object-cover rounded-md"
      onError={() => setHasError(true)}
    />
  );
};

const Destinations = () => {
  const queryClient = useQueryClient();

  // State management
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDestination, setSelectedDestination] = useState(null);
  const { searchQuery, setSearchQuery, activeSearch } = useTableSearch();
  const [destinationName, setDestinationName] = useState("");
  const [thumbImage, setThumbImage] = useState(null);
  const [coverImage, setCoverImage] = useState(null);
  const [destinationStatus, setDestinationStatus] = useState(1);
  const [displayWeb, setDisplayWeb] = useState(true);
  const [featured, setFeatured] = useState(false);
  const [showError, setShowError] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedState, setSelectedState] = useState({
    id: "",
    name: "",
    countryCode: "",
  });
  const [destinationLatitude, setDestinationLatitude] = useState("");
  const [destinationLongitude, setDestinationLongitude] = useState("");
  const [destinationPlaceId, setDestinationPlaceId] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [errors, setErrors] = useState({
    name: "",
    state: "",
    thumbImage: "",
    coverImage: "",
  });

  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeSearch]);

  // Queries
  const { data: destinationData } = useQuery({
    queryKey: ["destinations", currentPage, activeSearch],
    queryFn: () =>
      destinationService.list({ page: currentPage, search: activeSearch || undefined }),
    keepPreviousData: true,
  });

  const { data: stateData } = useQuery({
    queryKey: ["stateOptions"],
    queryFn: destinationService.getStateOptions,
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: destinationService.create,
    onSuccess: (success) => {
      setIsLoading(false);
      showSuccessMessage(success.response?.data?.message || "Destination added successfully!");
      setAddDialogOpen(false);
      resetForm();
      queryClient.invalidateQueries(["destinations"]);
    },
    onError: (error) => {
      setIsLoading(false);
      setErrorMessage(error.response?.data?.message || "Failed to add destination");
      setShowError(true);
    },
  });

  const updateMutation = useMutation({
    mutationFn: destinationService.update,
    onSuccess: (success) => {
      setIsLoading(false);
      showSuccessMessage(success.response?.data?.message || "Destination updated successfully!");
      setEditDialogOpen(false);
      resetForm();
      queryClient.invalidateQueries(["destinations"]);
    },
    onError: (error) => {
      setIsLoading(false);
      setErrorMessage(error.response?.data?.message || "Failed to update destination");
      setShowError(true);
    },
  });

  const getByIdMutation = useMutation({
    mutationFn: destinationService.getById,
    onSuccess: (data) => {
      setIsLoading(false);
      setSelectedDestination(data.data);
      setDestinationName(data.data.name);
      const findState = states.find(state => state.id === data.data.state_id);
      setSelectedState({ 
        id: findState?.id || "", 
        name: findState?.name || "",
        countryCode: findState?.country.code || "",
      });
      setDestinationStatus(data.data.status);
      setDestinationLatitude(data.data.latitude);
      setDestinationLongitude(data.data.longitude);
      setDestinationPlaceId(data.data.place_id);
      setDisplayWeb(data.data.display_web === 1);
      setFeatured(data.data.featured === 1);
      setThumbImage(null);
      setCoverImage(null);
      setEditDialogOpen(true);
    },
    onError: (error) => {
      setIsLoading(false);
      setErrorMessage(error.response?.data?.message || "Failed to fetch destination details");
      setShowError(true);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: destinationService.deleteDestination,
    onSuccess: (success) => {
      setIsLoading(false);
      showSuccessMessage(success.response?.data?.message || "Destination deleted successfully!");
      setDeleteDialogOpen(false);
      queryClient.invalidateQueries(["destinations"]);
    },
    onError: (error) => {
      setIsLoading(false);
      setErrorMessage(error.response?.data?.message || "Failed to delete destination");
      setShowError(true);
    },
  });

  // Helper functions
  const resetForm = () => {
    setDestinationName("");
    setSelectedState({
      id: "",
      name: "",
      countryCode: "",
    });
    setDestinationLatitude("");
    setDestinationLongitude("");
    setDestinationPlaceId("");
    setThumbImage(null);
    setCoverImage(null);
    setErrors({
      name: "",
      state: "",
      thumbImage: "",
      coverImage: "",
    });
  };

  const validateForm = () => {
    let tempErrors = {
      name: "",
      state: "",
      thumbImage: "",
      coverImage: "",
    };
    let isValid = true;

    if (!destinationName.trim()) {
      tempErrors.name = "Destination name is required";
      isValid = false;
    }

    if (!selectedState.id) {
      tempErrors.state = "Please select a state";
      isValid = false;
    }

    if (!thumbImage && !selectedDestination) {
      tempErrors.thumbImage = "Thumb image is required";
      isValid = false;
    }

    if (!coverImage && !selectedDestination) {
      tempErrors.coverImage = "Cover image is required";
      isValid = false;
    }

    setErrors(tempErrors);
    return isValid;
  };

  const showSuccessMessage = (message) => {
    setSuccessMessage(message);
    setShowSuccess(true);
    setTimeout(() => {
      setShowSuccess(false);
      setSuccessMessage("");
    }, 3000);
  };

  // Action handlers
  const handleAddClick = () => {
    resetForm();
    setDestinationStatus(1);
    setAddDialogOpen(true);
  };

  const handleDeleteClick = (destination) => {
    setSelectedDestination(destination);
    setDeleteDialogOpen(true);
  };

  const handleEditClick = (destinationId) => {
    setIsLoading(true);
    getByIdMutation.mutate(destinationId);
  };

  const handlePlaceSelect = (placeDetails) => {
    if (placeDetails) {
      setDestinationName(placeDetails.name);
      setDestinationLatitude(placeDetails.lat);
      setDestinationLongitude(placeDetails.lng);
      setDestinationPlaceId(placeDetails.place_id);
      setErrors({ ...errors, name: "" });
    } else {
      setDestinationName("");
      setDestinationLatitude("");
      setDestinationLongitude("");
      setDestinationPlaceId("");
    }
  };

  const handleSaveDestination = () => {
    setIsLoading(true);
    if (validateForm()) {
      const formData = new FormData();
      formData.append("name", destinationName);
      formData.append("state_id", selectedState.id);
      formData.append("status", destinationStatus);
      formData.append("latitude", destinationLatitude);
      formData.append("longitude", destinationLongitude);
      formData.append("place_id", destinationPlaceId);
      formData.append("display_web", displayWeb ? 1 : 0);
      formData.append("featured", featured ? 1 : 0);
      if (thumbImage) {
        formData.append("thumb_image", thumbImage);
      }
      if (coverImage) {
        formData.append("cover_image", coverImage);
      }
      createMutation.mutate(formData);
    } else {
      setIsLoading(false);
    }
  };

  const handleUpdateDestination = () => {
    if (validateForm()) {
      const formData = new FormData();
      formData.append("name", destinationName);
      formData.append("state_id", selectedState.id);
      formData.append("status", destinationStatus);
      formData.append("latitude", destinationLatitude);
      formData.append("longitude", destinationLongitude);
      formData.append("place_id", destinationPlaceId);
      formData.append("display_web", displayWeb ? 1 : 0);
      formData.append("featured", featured ? 1 : 0);
      if (thumbImage) {
        formData.append("thumb_image", thumbImage);
      }
      if (coverImage) {
        formData.append("cover_image", coverImage);
      }

      updateMutation.mutate({
        id: selectedDestination.id,
        formData,
      });
    }
  };

  const handleDeleteDestination = () => {
    setIsLoading(true);
    if (selectedDestination) {
      deleteMutation.mutate(selectedDestination.id);
    }
  };

  const handlePageChange = (page) => {
    if (page !== currentPage) {
      setCurrentPage(page);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const destinations = destinationData?.data?.data || [];
  const states = stateData?.data || [];

  return (
    <AdminPageLayout>
      {/* Error Toster */}
      {showError && (
        <Toster
          message={errorMessage}
          type="error"
          duration={3000}
          onClose={() => setShowError(false)}
        />
      )}

      {/* Success Toster */}
      {showSuccess && successMessage && (
        <Toster
          message={successMessage}
          type="success"
          duration={3000}
          onClose={() => {
            setShowSuccess(false);
            setSuccessMessage("");
          }}
        />
      )}

      <AdminPageHeader
        title={ADMIN_PAGE_META.destinations.title}
        subtitle={ADMIN_PAGE_META.destinations.subtitle}
      />

      <AdminPageCard>
        <AdminPageCardToolbar>
          <AdminTableToolbar className="mb-0">
            <TableSearchInput
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder="Search destinations..."
            />
            <button
              onClick={handleAddClick}
              className="bg-primary text-white px-4 py-1 rounded-lg border border-primary shadow-md hover:bg-primary/90 transition-all"
            >
              Add Destination
            </button>
          </AdminTableToolbar>
        </AdminPageCardToolbar>

        <AdminTable
          className="shadow-none rounded-none"
          footer={
            <AdminTableFooter>
              <Pagination
                pagination={{
                  currentPage: destinationData?.data?.current_page || 1,
                  lastPage: destinationData?.data?.last_page || 1,
                  perPage: destinationData?.data?.per_page || 10,
                  total: destinationData?.data?.total || 0,
                  from: destinationData?.data?.from || 0,
                  to: destinationData?.data?.to || 0,
                }}
                onPageChange={handlePageChange}
                showInfo={true}
              />
            </AdminTableFooter>
          }
        >
        <AdminTableHead>
          <AdminTableTh>#</AdminTableTh>
          <AdminTableTh>Preview</AdminTableTh>
          <AdminTableTh>Title</AdminTableTh>
          <AdminTableTh>State</AdminTableTh>
          <AdminTableTh>Status</AdminTableTh>
          <AdminTableTh>Display</AdminTableTh>
          <AdminTableTh>Featured</AdminTableTh>
          <AdminTableTh align="right">Actions</AdminTableTh>
        </AdminTableHead>
        <AdminTableBody>
          {destinations.length > 0 ? (
            destinations.map((destination, index) => (
              <AdminTableRow key={destination.id}>
                <AdminTableCell>{index + 1}</AdminTableCell>
                <AdminTableCell>
                  <DestinationThumbPreview destination={destination} />
                </AdminTableCell>
                <AdminTableCell>{destination.name}</AdminTableCell>
                <AdminTableCell>{destination.state?.name || "N/A"}</AdminTableCell>
                <AdminTableCell>
                  <StatusBadge variant={getStatusVariant(destination.status)}>
                    {destination.status === 1 ? "Active" : "In-Active"}
                  </StatusBadge>
                </AdminTableCell>
                <AdminTableCell>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    destination.display_web === 1 
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                  }`}>
                    {destination.display_web === 1 ? 'Shown' : 'Hidden'}
                  </span>
                </AdminTableCell>
                <AdminTableCell>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    destination.featured === 1 
                    ? 'bg-purple-100 text-purple-800'
                    : 'bg-gray-100 text-gray-800'
                  }`}>
                    {destination.featured === 1 ? 'Featured' : 'Regular'}
                  </span>
                </AdminTableCell>
                <TableActionsCell>
                  <EditActionButton
                    showLabel={false}
                    onClick={() => handleEditClick(destination.id)}
                  />
                  <DeleteActionButton
                    showLabel={false}
                    onClick={() => handleDeleteClick(destination)}
                  />
                </TableActionsCell>
              </AdminTableRow>
            ))
          ) : (
            <TableEmptyRow colSpan={8} emptyMessage="No destinations found" />
          )}
        </AdminTableBody>
      </AdminTable>
      </AdminPageCard>

      {/* Add Destination Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={() => {
          setAddDialogOpen(false);
          setIsLoading(false);
          setErrors({
            name: "",
            state: "",
            thumbImage: "",
            coverImage: "",
          });
        }}
        title={
          <div className="flex items-center gap-2">
            <i className="fi fi-bs-add"></i>
            Create Destination
          </div>
        }
        primaryButtonText="Save"
        primaryButtonAction={handleSaveDestination}
        closeButtonText="Close"
        closeButtonAction={() => {
          setAddDialogOpen(false);
          setIsLoading(false);
          setErrors({
            name: "",
            state: "",
            thumbImage: "",
            coverImage: "",
          });
        }}
        isLoading={isLoading}
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select State <span className="text-red-500">*</span>
              </label>
              <select
                className={`text-sm w-full px-3 py-1 border ${
                  errors.state ? "border-red-500" : "border-gray-300"
                } rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary`}
                value={selectedState.id}
                onChange={(e) => {
            
                  const findState = states.find(state => Number(state.id) === Number(e.target.value));

                  setSelectedState({
                    id: findState?.id || "",
                    name: findState?.name || "",
                    countryCode: findState?.country?.code || "",
                  });
                  setErrors({ ...errors, state: "" });
                }}
              >
                <option value="">Select a state</option>
                {states.map((state) => (
                  <option key={state.id} value={state.id}>
                    {state.name}
                  </option>
                ))}
              </select>
              {errors.state && (
                <p className="text-red-500 text-xs mt-1">{errors.state}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Destination Name <span className="text-red-500">*</span>
              </label>
              <PlaceSearchInput
                value={destinationName}
                onChange={handlePlaceSelect}
                placeholder="Search for a destination..."
                hasError={!!errors.name}
                types={['(cities)']}
                componentRestrictions={selectedState.countryCode ? { country: selectedState.countryCode } : undefined}
              />
              {errors.name && (
                <p className="text-red-500 text-xs mt-1">{errors.name}</p>
              )}
            </div>
          </div>

          <div>
            <div className="mb-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                className="text-sm w-full px-3 grid-cols-2 py-1 border border-gray-300 rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                value={destinationStatus}
                onChange={(e) => setDestinationStatus(Number(e.target.value))}
              >
                <option value={1}>Active</option>
                <option value={0}>In-Active</option>
              </select>
            </div>
          </div>

          <div className="mb-4">
            <ToggleButton
              enabled={displayWeb}
              onChange={setDisplayWeb}
              label="Show destination on website?"
            />
          </div>

          <div className="mb-4">
            <ToggleButton
              enabled={featured}
              onChange={setFeatured}
              label="Set as featured destination?"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <ImageInput
                onChange={(image) => {
                  setThumbImage(image);
                  setErrors({ ...errors, thumbImage: "" });
                }}
                showLabel={true}
                label="Thumbnail Image"
                required={true}
                error={errors.thumbImage}
              />
              {errors.thumbImage && (
                <p className="text-red-500 text-xs mt-1">{errors.thumbImage}</p>
              )}
            </div>

            <div>
              <ImageInput
                onChange={(image) => {
                  setCoverImage(image);
                  setErrors({ ...errors, coverImage: "" });
                }}
                showLabel={true}
                label="Cover Image"
                required={true}
                error={errors.coverImage}
              />
              {errors.coverImage && (
                <p className="text-red-500 text-xs mt-1">{errors.coverImage}</p>
              )}
            </div>
          </div>

          {/* Preview Section */}
          <div className="mt-4">
            <label className="block font-bold text-sm text-gray-700 mb-1">
              Attachment preview
            </label>
            <div className="grid grid-cols-2 gap-4">
              {thumbImage && (
                <FilePreview
                  file={thumbImage}
                  label="Thumbnail Image"
                  onDelete={() => setThumbImage(null)}
                  isImage={true}
                  showDelete={false}
                />
              )}
              {coverImage && (
                <FilePreview
                  file={coverImage}
                  label="Cover Image"
                  onDelete={() => setCoverImage(null)}
                  isImage={true}
                  showDelete={false}
                />
              )}
            </div>
          </div>
        </div>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        title={
          <div className="flex items-center gap-2">
            <i className="fi fi-rr-pencil text-[16px]"></i>
            Edit Destination
          </div>
        }
        primaryButtonText="Update"
        primaryButtonAction={handleUpdateDestination}
        closeButtonText="Cancel"
        closeButtonAction={() => setEditDialogOpen(false)}
        isLoading={isLoading}
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select State <span className="text-red-500">*</span>
              </label>
              <select
                className={`text-sm w-full px-3 py-1 border ${
                  errors.state ? "border-red-500" : "border-gray-300"
                } rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary`}
                value={selectedState.id}
                onChange={(e) => {
                  const findState = states.find(state => Number(state.id) === Number(e.target.value));
                  setSelectedState({
                    id: findState?.id || "",
                    name: findState?.name || "",
                    countryCode: findState?.country?.code || "",
                  });
                  setErrors({ ...errors, state: "" });
                }}
              >
                <option value="">Select a state</option>
                {states.map((state) => (
                  <option key={state.id} value={state.id}>
                    {state.name}
                  </option>
                ))}
              </select>
              {errors.state && (
                <p className="text-red-500 text-xs mt-1">{errors.state}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Destination Name <span className="text-red-500">*</span>
              </label>
              <PlaceSearchInput
                value={destinationName}
                onChange={handlePlaceSelect}
                placeholder="Search for a destination..."
                hasError={!!errors.name}
                types={["(cities)"]}
                componentRestrictions={selectedState.countryCode ? { country: selectedState.countryCode } : undefined}
              />
              {errors.name && (
                <p className="text-red-500 text-xs mt-1">{errors.name}</p>
              )}
            </div>
          </div>

          <div>
            <div className="mb-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                className="text-sm w-full px-3 grid-cols-2 py-1 border border-gray-300 rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                value={destinationStatus}
                onChange={(e) => setDestinationStatus(Number(e.target.value))}
              >
                <option value={1}>Active</option>
                <option value={0}>In-Active</option>
              </select>
            </div>
          </div>

          <div className="mb-4">
            <ToggleButton
              enabled={displayWeb}
              onChange={setDisplayWeb}
              label="Show destination on website?"
            />
          </div>

          <div className="mb-4">
            <ToggleButton
              enabled={featured}
              onChange={setFeatured}
              label="Set as featured destination?"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <ImageInput
                onChange={(image) => {
                  setThumbImage(image);
                  setErrors({ ...errors, thumbImage: "" });
                }}
                showLabel={true}
                label="Thumbnail Image"
                error={errors.thumbImage}
              />
              {errors.thumbImage && (
                <p className="text-red-500 text-xs mt-1">{errors.thumbImage}</p>
              )}
            </div>

            <div>
              <ImageInput
                onChange={(image) => {
                  setCoverImage(image);
                  setErrors({ ...errors, coverImage: "" });
                }}
                showLabel={true}
                label="Cover Image"
                error={errors.coverImage}
              />
              {errors.coverImage && (
                <p className="text-red-500 text-xs mt-1">{errors.coverImage}</p>
              )}
            </div>
          </div>

          {/* Preview Section */}
          <div className="mt-4">
            <label className="block font-bold text-sm text-gray-700 mb-1">
              Attachment preview
            </label>
            <div className="grid grid-cols-2 gap-4">
              {thumbImage ? (
                <FilePreview
                  file={thumbImage}
                  label="Thumbnail Image"
                  onDelete={() => setThumbImage(null)}
                  isImage={true}
                  showDelete={true}
                />
              ) : selectedDestination?.thumb_image_url ? (
                <FilePreview
                  fileUrl={selectedDestination.thumb_image_url}
                  label="Current Thumbnail Image"
                  isImage={true}
                  showDelete={false}
                />
              ) : (
                <div className="text-sm text-gray-500">No thumb image selected</div>
              )}

              {coverImage ? (
                <FilePreview
                  file={coverImage}
                  label="Cover Image"
                  onDelete={() => setCoverImage(null)}
                  isImage={true}
                  showDelete={true}
                />
              ) : selectedDestination?.cover_image_url ? (
                <FilePreview
                  fileUrl={selectedDestination.cover_image_url}
                  label="Current Cover Image"
                  isImage={true}
                  showDelete={false}
                />
              ) : (
                <div className="text-sm text-gray-500">No cover image selected</div>
              )}
            </div>
          </div>
        </div>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        title={
          <div className="flex items-center gap-2">
            <i className="fi fi-rr-trash text-[16px]"></i>
            Delete Destination
          </div>
        }
        primaryButtonText="Delete"
        primaryButtonAction={handleDeleteDestination}
        closeButtonText="Cancel"
        closeButtonAction={() => setDeleteDialogOpen(false)}
        isLoading={isLoading}
      >
        <div className="mt-2">
          <p className="text-sm text-gray-500">
            Are you sure you want to delete this destination? This action cannot be
            undone.
          </p>
        </div>
      </Dialog>
    </AdminPageLayout>
  );
};

export default Destinations;
